/// <amd-module name="Build/Run/Components/JobsTable" />
import * as React from "react";

import { TimelineRecordState} from "Build/Client/Contracts/Build";
import { RunDetailsData, RunJobData, RunStageData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Run/Resources";

import { IRunDetailsService } from "Build/Run/RunDetailsService.types";

import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";

import { getTimelineStatusIndicatorData } from "Build/Common/Library/Indicator";

import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format, equals } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { Icon } from "VSSUI/Icon";

import { Duration } from "VSSUI/Duration";
import { Status, StatusSize } from "VSSUI/Status";
import { Table, ITableColumn, TableColumnStyle, SimpleTableCell, ITableRow, ITableBreakpoint } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

import { ObservableArray, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { CheckStatus, ManualValidationStatus } from "./CheckStatus";
import { CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import * as Constants from "Build/Run/Constants";

export interface IJobsTableProps extends IVssComponentProperties {
    onReviewStageClicked: (stage: RunStageData) => void;
    onReviewManualValidationClicked: (stage: RunStageData) => void;
}

export interface IJobsTableState {
    run: RunDetailsData;
    columns: ITableColumn<RunJobData>[];
    breakpoints: ITableBreakpoint[];
    showApprovalStatus: boolean
}

export class JobsTable extends VssComponent<IJobsTableProps, IJobsTableState> {
    private _service: IRunDetailsService;
    private _observable: IReadonlyObservableValue<RunDetailsData>;
    private _showCheckIssues: Boolean;

    constructor(props: IJobsTableProps, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IRunDetailsService>("IRunDetailsService");
        this._observable = this._service.data;

        this._showCheckIssues = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1BuildCheckIssues", false)

        let columns: ITableColumn<RunJobData>[] = [
            this._nameColumn,
            this._statusColumn
        ];

        let breakpoints: ITableBreakpoint[] = [];

        if (this._service.data.value.stages.length > 1) {
            columns.push(this._statusIconColumn);
            columns.push(this._nameMobileColumn);
            columns.push(this._stageColumn);
            columns.push(this._durationColumn);

            breakpoints = [
                { breakpoint: ScreenBreakpoints.xsmall, columnWidths: [0, 0, 30, -100, 0, 100] },
                { breakpoint: ScreenBreakpoints.small, columnWidths: [-67, 132, 0, 0, 132, -33] },
            ];
        }
        else {
            columns.push(this._durationColumn);

            breakpoints = [
                { breakpoint: ScreenBreakpoints.xsmall, columnWidths: [-100, 0, 120] },
                { breakpoint: ScreenBreakpoints.small, columnWidths: [-67, 132, -33] },
            ];
        }

        this.state = {
            run: this._service.data.value,
            columns: columns,
            breakpoints: breakpoints,
            showApprovalStatus: (this._service.data.value.stages.length == 1)
        };
    }

    public render() {
        let itemProvider = new ObservableArray(this.state.run.jobs);
        const stage = this.state.run.stages[0];
        const onReviewApprovalsClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            this.props.onReviewStageClicked(stage);
        }

        const onReviewManualValidationClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            this.props.onReviewManualValidationClicked(stage);
        }

        let rejectedChecks = 0;
        let rejectedManualValidations = 0;

        if (stage.checks) {
            rejectedChecks = stage.checks.filter(x => x.status === CheckRunStatus.Rejected && !equals(Constants.ManualValidationTaskTypeIdString, x.typeId, true)).length;
            rejectedManualValidations = stage.checks.filter(x => equals(Constants.ManualValidationTaskTypeIdString, x.typeId, true) && x.status === CheckRunStatus.Rejected).length;
        }

        return (
            <div>
                {this.state.showApprovalStatus &&
                    <div className="flex-row margin-left-16 margin-top-4">
                    <CheckStatus 
                        checks={stage.checks || []} 
                        rejectedChecks={rejectedChecks} 
                        showIssues={this._showCheckIssues} 
                        onClick={onReviewApprovalsClick} />
                    <ManualValidationStatus 
                        checks={stage.checks || []} 
                        rejectedManualValidations={rejectedManualValidations} 
                        onClick={onReviewManualValidationClick} />
                    </div>
                }
                <Table ariaLabel={Resources.JobsTitle} itemProvider={itemProvider} tableBreakpoints={this.state.breakpoints} columns={this.state.columns} onActivate={this._onJobClick} />
            </div>
        );
    }

    public componentDidMount() {
        this._observable.subscribe(this._onObservableChanged);
    }

    public componentWillUnmount() {
        this._observable.unsubscribe(this._onObservableChanged);
    }

    private _onObservableChanged = (data: RunDetailsData) => {
        this.setState({
            run: data
        })
    }

    private _onSize(event: MouseEvent, index: number, width: number, column: ITableColumn<RunJobData>) {
        (column.width as ObservableValue<number>).value = width;
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        const status = getTimelineStatusIndicatorData(
            tableItem.state,
            tableItem.result,
            tableItem.stateData ? tableItem.stateData.pendingChecks : undefined,
            tableItem.stateData ? !tableItem.stateData.pendingDependencies : undefined
        );
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontWeightSemiBold fontSizeM scroll-hidden">
                <Tooltip text={status.label}>
                    <span className="flex-row">
                        <Status {...status.statusProps} className="icon-large-margin" size={StatusSize.m} />
                    </span>
                </Tooltip>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={tableItem.name}>
                        <span className="text-ellipsis">{tableItem.name}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderNameMobileCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        const displayName = format(Resources.JobsNameMobileFormat, tableItem.stageName, tableItem.name);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontWeightSemiBold fontSizeM scroll-hidden">
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={displayName}>
                        <span className="text-ellipsis">{displayName}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderStatusCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        const status = getTimelineStatusIndicatorData(
            tableItem.state,
            tableItem.result,
            tableItem.stateData ? tableItem.stateData.pendingChecks : undefined,
            tableItem.stateData ? !tableItem.stateData.pendingDependencies : undefined
        );
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontSizeM scroll-hidden">
                <Tooltip overflowOnly={true} text={status.label}>
                    <span className="text-ellipsis">{status.label}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderStatusIconCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        const status = getTimelineStatusIndicatorData(
            tableItem.state,
            tableItem.result,
            tableItem.stateData ? tableItem.stateData.pendingChecks : undefined,
            tableItem.stateData ? !tableItem.stateData.pendingDependencies : undefined
        );
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip text={status.label}>
                    <span className="flex-row">
                        <Status {...status.statusProps} className="icon-large-margin" size={StatusSize.m} />
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderStageCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontSizeM scroll-hidden">
                <Tooltip overflowOnly={true} text={tableItem.stageName}>
                    <span className="text-ellipsis">{tableItem.stageName}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderDurationCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RunJobData>, tableItem: RunJobData): JSX.Element => {
        if (!tableItem.startTime) {
            let message = Resources.JobHasNotStarted;
            if (tableItem.state === TimelineRecordState.Completed) {
                message = Resources.JobDidNotRun;
            }
            return (
                <SimpleTableCell
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    key={"col-" + columnIndex}
                    contentClassName="fontSizeM scroll-hidden secondary-text"
                    ariaLabel={message} />
            );
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontSizeM scroll-hidden secondary-text">
                <Icon iconName="Clock" className="icon-margin fontSizeML jobs-table-icon" />
                <Duration startDate={tableItem.startTime} endDate={tableItem.finishTime} />
            </SimpleTableCell>
        );
    }

    private _onJobClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, tableRow: ITableRow<RunJobData>) => {
        if (tableRow.data) {
            onClickFPS(this.context.pageContext, tableRow.data.linkProps.href, true, event);
        }
    }

    private _nameColumn: ITableColumn<RunJobData> = {
        id: "name",
        width: new ObservableValue(-67),
        name: Resources.JobColumnName,
        className: "jobs-cell",
        renderCell: this._renderNameCell,
        columnStyle: TableColumnStyle.Primary,
        onSize: this._onSize,
    };

    private _nameMobileColumn: ITableColumn<RunJobData> = {
        id: "name-mobile",
        width: new ObservableValue(-67),
        name: Resources.JobColumnName,
        className: "jobs-cell",
        renderCell: this._renderNameMobileCell,
        onSize: this._onSize,
    };

    private _statusColumn: ITableColumn<RunJobData> = {
        id: "status",
        width: new ObservableValue(132),
        name: Resources.StatusColumnName,
        className: "jobs-cell",
        renderCell: this._renderStatusCell
    };

    private _statusIconColumn: ITableColumn<RunJobData> = {
        id: "status-icon",
        width: new ObservableValue(30),
        className: "jobs-cell",
        renderCell: this._renderStatusIconCell
    };

    private _durationColumn: ITableColumn<RunJobData> = {
        id: "duration",
        width: new ObservableValue(-33),
        name: Resources.DurationColumnName,
        className: "jobs-cell",
        renderCell: this._renderDurationCell
    };

    private _stageColumn: ITableColumn<RunJobData> = {
        id: "stage",
        width: new ObservableValue(132),
        name: Resources.StageColumnName,
        className: "jobs-cell",
        renderCell: this._renderStageCell
    };
}